/**
 * Bitcoin units + display formatting — pure functions, NO React imports.
 *
 * Intl formatters are constructed once at module load: building one per render
 * is a measurable cost in a tab that is meant to stay open for days.
 */

/** Satoshis in one bitcoin. Consensus constant. */
export const SATS_PER_BTC = 100_000_000;

/**
 * Sats per dollar — spec.md: `100,000,000 / current price`.
 * Returns null for a non-positive or non-finite price rather than Infinity,
 * so the caller renders an em-dash instead of "∞ sats".
 */
export function satsPerDollar(price: number): number | null {
  if (!Number.isFinite(price) || price <= 0) return null;
  return SATS_PER_BTC / price;
}

export function btcToSats(btc: number): number {
  return Math.round(btc * SATS_PER_BTC);
}

export function satsToBtc(sats: number): number {
  return sats / SATS_PER_BTC;
}

/* ------------------------------------------------------------------ */
/* Formatters                                                          */
/* ------------------------------------------------------------------ */

const usd0 = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const usd2 = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const usdCompact = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const int = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

/** "$84,700" — the price hero. Cents are noise at this magnitude. */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return Math.abs(value) < 10 ? usd2.format(value) : usd0.format(value);
}

/** "$1.2M" — axis labels and the stack total when it gets long. */
export function formatUsdCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return usdCompact.format(value);
}

/** "1,181" — sats per dollar, whole sats. */
export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return int.format(Math.round(value));
}

/** "12.5M" — total sats, which routinely runs to nine digits. */
export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return compact.format(value);
}

/** "0.34821234" — BTC amounts, trailing zeros trimmed, 8dp max. */
export function formatBtc(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const fixed = value.toFixed(8);
  const trimmed = fixed.replace(/\.?0+$/, '');
  return trimmed === '' || trimmed === '-' ? '0' : trimmed;
}

/** "+1.24%" / "−0.80%" — always signed, so the sign carries the meaning. */
export function formatSignedPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(2)}%`;
}

const clock = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const monthYear = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' });
const yearOnly = new Intl.DateTimeFormat(undefined, { year: 'numeric' });

/** "8:42 AM" — the last-updated indicator. */
export const formatClock = (ms: number): string => clock.format(new Date(ms));
/** "Apr 2028" — the halving ETA. */
export const formatMonthYear = (ms: number): string => monthYear.format(new Date(ms));
/** "2018" — the chart's X axis, where only the decade-scale trend is legible. */
export const formatYear = (ms: number): string => yearOnly.format(new Date(ms));

/** "just now" / "4m ago" / "2h ago" — freshness next to a cached price. */
export function formatAge(fromMs: number, nowMs: number = Date.now()): string {
  const secs = Math.max(0, Math.round((nowMs - fromMs) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
